const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CloseIcon } = require('@mui/icons-material/Close');
const { default: DownloadIcon } = require('@mui/icons-material/Download');
const FocusIndicator = require('components/FocusIndicator');

const internals = {};

const { useState, useCallback } = React;

module.exports = function MediaViewer(props) {

    const {
        isOpen,
        onClose,
        imageUrl,
        imageAlt = 'Image'
    } = props;

    const {
        StyledDialog,
        Header,
        ImageContainer,
        StyledImage,
        ActionButton
    } = internals;

    const [isLoading, setIsLoading] = useState(true);

    const handleClose = useCallback(() => {

        FocusIndicator.onBlurHandler();
        setIsLoading(true);
        onClose?.();
    }, [onClose]);

    const handleDownload = useCallback(async () => {

        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = imageUrl.split('/').pop() || 'image.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Error downloading image:', error);
        }
    }, [imageUrl]);

    const handleImageLoad = useCallback(() => {

        setIsLoading(false);
    }, []);

    return (
        <StyledDialog
            open={isOpen}
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            aria-labelledby='media-viewer-dialog'
            onClick={handleClose}
        >
            <Header>
                <ActionButton
                    aria-label='Download image'
                    onClick={(e) => {

                        e.stopPropagation();
                        handleDownload();
                    }}
                    data-focus-outline='radius:20'
                >
                    <DownloadIcon />
                </ActionButton>
                <ActionButton
                    aria-label='Close'
                    onClick={(e) => {

                        e.stopPropagation();
                        handleClose();
                    }}
                    data-focus-outline='radius:20'
                >
                    <CloseIcon />
                </ActionButton>
            </Header>
            <ImageContainer>
                {isLoading && (
                    <div style={{ color: 'white', fontSize: '16px' }}>Loading...</div>
                )}
                <StyledImage
                    src={imageUrl}
                    alt={imageAlt}
                    onLoad={handleImageLoad}
                    style={{ display: isLoading ? 'none' : 'block' }}
                    tabIndex={0}
                    aria-label="Close image"
                    data-focus-outline='radius:8'
                    onKeyDown={(e) => {

                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClose();
                        }
                    }}
                />
            </ImageContainer>
        </StyledDialog>
    );
};

module.exports.propTypes = {
    isOpen: T.bool,
    onClose: T.func,
    imageUrl: T.string,
    imageAlt: T.string
};

internals.StyledDialog = Styled(Dialog)`
    .MuiDialog-paper {
        background-color: rgba(0, 0, 0, 0);
        box-shadow: none;
        max-width: 100%;
        max-height: 100%;
        margin: 0;
    }

    .MuiBackdrop-root {
        background-color: rgba(0, 0, 0, 0.75);
    }
`;

internals.Header = Styled.div`
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1300;
    display: flex;
    gap: 8px;
    padding: 16px;
    background: black;
    border-bottom-left-radius: 15px;
`;

internals.ActionButton = Styled(IconButton)`
    color: white !important;
    background-color: rgba(255, 255, 255, 0.1) !important;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.2) !important;
    }
`;

internals.ImageContainer = Styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100vh;
    padding: 20px;
`;

internals.StyledImage = Styled.img`
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 40px);
    object-fit: contain;
    user-select: none;
`;
